import type { ExcludePayload, ExcludeReasonKey } from '../interface/exclude';

/**
 * Canonical reason order, declared once: `ExcludeModal` renders the pills in it and this normalizer emits
 * `payload.reasons` in it, so the payload is deterministic regardless of click order.
 */
export const EXCLUDE_REASON_ORDER: readonly ExcludeReasonKey[] = [
  'excludedProvider',
  'excludedSpecialty',
  'excludedFacility',
  'duplicate',
  'other'
];

export interface ExcludeFormValues {
  readonly reasons: readonly ExcludeReasonKey[];
  /** Dictionary codes chosen under "Other"; ignored unless `other` is among `reasons`. */
  readonly otherCodes: readonly string[];
  readonly note: string;
  readonly caseContext: string;
}

export type ExcludeValidationError = 'reason-required' | 'other-code-required';

type Result =
  | { ok: true; payload: ExcludePayload }
  | { ok: false; errors: ExcludeValidationError[] };

/**
 * Single source of truth for both the submit path and the modal's live per-field error flags.
 *
 * `availableOtherCodes` is a parameter because selecting Other requires a code ONLY when a vocabulary
 * exists; no environment registers one today, so an unconditional rule would strand every coder. Selected
 * codes are INTERSECTED with it, not counted, so a stale or fabricated code cannot cross the api boundary.
 *
 * Narrow with `result.ok === true`, never `!result.ok`: under this repo's `strict: false`, negation does
 * not narrow a discriminated union (TS2339 on `.errors`).
 */
export function buildExcludePayload(
  values: ExcludeFormValues,
  availableOtherCodes: readonly string[]
): Result {
  const errors: ExcludeValidationError[] = [];

  // De-duplicate and impose the declared display order. Filtering the canonical order by membership does
  // both in one pass, and makes the output independent of click order by construction rather than by a sort
  // that a later edit could drop.
  const reasons = EXCLUDE_REASON_ORDER.filter((key) =>
    values.reasons.includes(key)
  );
  if (reasons.length === 0) errors.push('reason-required');

  const hasOther = reasons.includes('other');
  // Same construction for codes: walk the vocabulary, keep what was selected. Anything selected that the
  // vocabulary does not contain is silently dropped -- it cannot be honoured, and forwarding it would put an
  // unresolvable identifier into an audit record.
  //
  // The vocabulary is de-duplicated FIRST. Filtering it directly would faithfully reproduce a duplicate
  // supplied by a malformed dictionary, so `otherCodes` could contain the same code twice while this
  // module's contract promises de-duplication. First-seen order is preserved.
  const uniqueAvailable = availableOtherCodes.filter(
    (code, i) => availableOtherCodes.indexOf(code) === i
  );
  const otherCodes = hasOther
    ? uniqueAvailable.filter((code) => values.otherCodes.includes(code))
    : [];
  if (hasOther && uniqueAvailable.length > 0 && otherCodes.length === 0) {
    errors.push('other-code-required');
  }

  const note = values.note.trim();
  const caseContext = values.caseContext.trim();

  if (errors.length > 0) return { ok: false, errors };

  const payload: ExcludePayload = {
    reasons,
    ...(otherCodes.length > 0 ? { otherCodes } : {}),
    ...(note.length > 0 ? { note } : {}),
    ...(caseContext.length > 0 ? { caseContext } : {})
  };
  return { ok: true, payload };
}
